/**
 * SolutionEstimateRoleMap — Settings ▸ Labor Categories ▸ "Solution estimate roles".
 *
 * Roles reported by the Solution API's project estimates, mapped onto this app's Labor
 * Categories so a plan can price an imported estimate. New roles appear here the first
 * time a Resource Plan pulls in an estimate that reports one Nymbl Staff hasn't seen
 * before — this table has no create action of its own.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLaborCategories } from "@/hooks/use-rate-card";
import {
  useSetSolutionEstimateRoleMapping,
  useSolutionEstimateRoles,
} from "@/hooks/use-solution-estimate-roles";

export function SolutionEstimateRoleMap() {
  const { data: roles = [], isLoading } = useSolutionEstimateRoles();
  const { data: categories = [] } = useLaborCategories();
  const setMapping = useSetSolutionEstimateRoleMapping();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Solution estimate roles</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Roles reported by Solution estimates, mapped onto this app&rsquo;s Labor Categories. New
          roles appear here automatically the first time a Resource Plan pulls in a Solution
          estimate that reports one Nymbl Staff hasn&rsquo;t seen before.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : roles.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No roles captured yet — they&rsquo;ll appear here the first time a Resource Plan imports
            a Solution estimate.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {roles.map((role) => {
              // An inactive Labor Category is a dead end as a NEW mapping target — the
              // import backend skips inactive categories entirely and reports their roles
              // as unmapped — so only active categories are offered. A role's CURRENT
              // mapping is kept visible even if that category has since been deactivated,
              // annotated the same way RateCardManager flags an incomplete Rate Basis.
              const activeCategories = categories.filter((c) => c.active);
              const mappedInactive =
                role.laborCategoryId !== null &&
                !activeCategories.some((c) => c.id === role.laborCategoryId)
                  ? categories.find((c) => c.id === role.laborCategoryId)
                  : undefined;
              return (
                <div
                  key={role.id}
                  className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-medium">{role.externalRoleName}</p>
                    <p className="text-xs text-muted-foreground">{role.externalRoleId}</p>
                  </div>
                  <select
                    aria-label={`Labor Category for ${role.externalRoleName}`}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={role.laborCategoryId ?? ""}
                    disabled={setMapping.isPending}
                    onChange={(e) =>
                      setMapping.mutate({
                        id: role.id,
                        laborCategoryId: e.target.value === "" ? null : e.target.value,
                      })
                    }
                  >
                    <option value="">Unmapped</option>
                    {mappedInactive !== undefined && (
                      <option value={mappedInactive.id}>{mappedInactive.name} (inactive)</option>
                    )}
                    {activeCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
